import axios from 'axios'
import { getJWToken, refreshMySession } from '@utils'
import { toast } from 'react-toastify'
import Toast from '@comps/Toast'

const api = axios.create({ baseURL: process.env.REACT_APP_BASEURL })

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(p => {
    if (error) p.reject(error)
    else p.resolve(token)
  })
  failedQueue = []
}

api.interceptors.response.use(
  response => response,
  async error => {
    const { config, response } = error
    const originalRequest = config

    if (response && response.status === 401) {
      // If we are already refreshing, add to queue
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return axios(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      isRefreshing = true

      toast.warning(<Toast title='Refreshing session ...' />, {
        toastId: 'refresh-session',
        autoClose: 3000,
        hideProgressBar: false,
        closeButton: true
      })

      try {
        const refreshed = await refreshMySession()
        if (refreshed) {
          const jwt = getJWToken()
          api.defaults.headers.common.Authorization = `Bearer ${jwt}`
          originalRequest.headers.Authorization = `Bearer ${jwt}`

          processQueue(null, jwt)
          return axios(originalRequest)
        } else {
          processQueue(error)
          toast.error(<Toast title='Session expired, kindly login again' />, {
            toastId: 'session-expired',
            autoClose: 4000,
            hideProgressBar: false,
            closeButton: false
          })
          return Promise.reject(error)
        }
      } catch (refreshError) {
        processQueue(refreshError)
        toast.error(<Toast title='Session expired, kindly login again' />, {
          toastId: 'session-expired',
          autoClose: 4000,
          hideProgressBar: false,
          closeButton: false
        })
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    return Promise.reject(error)
  }
)

export const request = ({ ...options }) => {
  if (options.url !== '/generic/slots') {
    const jwt = getJWToken()
    if (jwt) api.defaults.headers.common.Authorization = `Bearer ${jwt}`
  }

  const onSuccess = async (response) => { return response }

  const onError = (error) => {
    console.log('~ onError error:', error)
    if (error.response && error.response.status === 403) toast.error(<Toast title='Unauthorised access, contact your admin.' />, {
      autoClose: 3000,
      hideProgressBar: false,
      closeButton: false
    })
    else if (error.response && error.response.status === 500) toast.error(<Toast
      title={error.response.data ? error.response.data.error ? error.response.data.error : error.response.data : 'Server error, contact admin.'} />, {
      autoClose: 3000,
      hideProgressBar: false,
      closeButton: false
    })
    else if (error && error.message === 'Network Error') toast.error(<Toast
      title='Oops, server unreachable, please check your internet connection or try again in a few minutes' />, {
      toastId: 'network-error',
      autoClose: 5000,
      hideProgressBar: false,
      closeButton: false
    })
    else toast.error(<Toast title={`Oops! ${error.message}`} />, {
      toastId: `unknown-error-${error.message}`,
      autoClose: 3000,
      hideProgressBar: false,
      closeButton: false
    })
    return Promise.reject(error)
  }

  return api(options).then(onSuccess).catch(onError)
}